import { api } from '../utils/api';

/**
 * Frontend Data Service API Client
 * Provides methods to interact with backend data providers
 */

/**
 * Get list of all registered data providers
 * @returns {Promise<Array>} Array of provider information
 */
export const listProviders = async () => {
  try {
    const response = await api.get('/data/providers');
    // api.get returns parsed JSON directly, not wrapped in { data: ... }
    return response?.providers || [];
  } catch (error) {
    console.error('Error fetching providers:', error);
    throw new Error(error.message || 'Failed to fetch providers');
  }
};

/**
 * Search for data across providers
 * @param {object} query - Search query { keywords, filters }
 * @param {Array<string>} providers - Optional array of provider IDs to use
 * @param {object} options - Search options { maxResults, language, etc }
 * @returns {Promise<object>} Search results and metadata
 */
export const search = async (query, providers = null, options = {}) => {
  try {
    const response = await api.post('/data/search', {
      query,
      providers,
      options
    });
    
    return {
      results: response?.results || [],
      metadata: response?.metadata || {}
    };
  } catch (error) {
    console.error('Error searching data:', error);
    throw new Error(error.message || 'Search failed');
  }
};

/**
 * Enrich entity data using providers
 * @param {string} entityType - Entity type (Customer, Lead, etc.)
 * @param {string} entityId - Entity ID
 * @param {object} entityData - Existing entity data
 * @param {Array<string>} providers - Optional array of provider IDs to use
 * @param {object} options - Enrichment options
 * @returns {Promise<object>} Enrichment results and metadata
 */
export const enrich = async (entityType, entityId, entityData, providers = null, options = {}) => {
  try {
    const response = await api.post('/data/enrich', {
      entityType,
      entityId,
      entityData,
      providers,
      options
    });
    
    return {
      enrichments: response?.enrichments || [],
      metadata: response?.metadata || {}
    };
  } catch (error) {
    console.error('Error enriching data:', error);
    throw new Error(error.message || 'Enrichment failed');
  }
};

/**
 * Scout for related leads using providers
 * @param {string} entityType - Entity type (Customer, Lead, etc.)
 * @param {string} entityId - Entity ID
 * @param {object} entityData - Existing entity data
 * @param {Array<string>} providers - Optional array of provider IDs to use
 * @param {object} options - Scout options
 * @returns {Promise<object>} Scout results and metadata
 */
export const scout = async (entityType, entityId, entityData, providers = null, options = {}) => {
  try {
    const response = await api.post('/data/scout', {
      entityType,
      entityId,
      entityData,
      providers,
      options
    });
    
    return {
      results: response?.results || [],
      metadata: response?.metadata || {}
    };
  } catch (error) {
    console.error('Error scouting data:', error);
    throw new Error(error.message || 'Scout failed');
  }
};

/**
 * Test a specific provider
 * @param {string} providerId - Provider ID to test
 * @returns {Promise<object>} Test results
 */
export const testProvider = async (providerId) => {
  try {
    const response = await api.post(`/data/test/${providerId}`);
    return response;
  } catch (error) {
    console.error(`Error testing provider ${providerId}:`, error);
    throw new Error(error.message || 'Provider test failed');
  }
};

/**
 * Get provider configurations (admin only)
 * @returns {Promise<object>} Provider configurations
 */
export const getConfig = async () => {
  try {
    const response = await api.get('/data/config');
    return response?.config || {};
  } catch (error) {
    console.error('Error fetching config:', error);
    // If user is not admin, config is optional—return empty object
    if (error.status === 403 || (error.message && error.message.includes('Admin access required'))) {
      return { providers: {}, permissions: 'forbidden' };
    }
    throw new Error(error.message || 'Failed to fetch config');
  }
};

/**
 * Update provider configuration (admin only)
 * @param {string} providerId - Provider ID to update
 * @param {object} updates - Configuration updates
 * @returns {Promise<object>} Update result
 */
export const updateConfig = async (providerId, updates) => {
  try {
    const response = await api.put(`/data/config/${providerId}`, updates);
    return response;
  } catch (error) {
    console.error(`Error updating config for ${providerId}:`, error);
    throw new Error(error.message || 'Failed to update config');
  }
};

/**
 * Helper: Extract leads from search results in standardized format
 * Converts provider-specific response format to Lead entity format
 * @param {Array} results - Search results from data service
 * @returns {Array} Array of leads in Lead entity format
 */
export const convertResultsToLeads = (results) => {
  if (!Array.isArray(results)) {
    return [];
  }

  return results.map(result => {
    const data = result.data || {};
    const source = result.source || {};
    
    // Determine source label
    let sourceLabel = source.providerName || source.provider || 'Unknown';
    if (source.provider === 'govspending') {
      sourceLabel = 'GovSpending API';
    } else if (source.provider === 'tavily') {
      sourceLabel = 'Tavily Search';
    }

    return {
      // Core lead fields
      ContactName: data.contactName || data.title || 'Unknown Contact',
      CompanyName: data.companyName || '',
      ContactEmail: data.contactEmail || data.contactInfo?.email || '',
      ContactPhone: data.contactPhone || data.contactInfo?.phone || '',
      Website: data.website || data.url || data.contactInfo?.website || '',
      JobTitle: data.jobTitle || '',
      Industry: data.industry || '',
      Location: data.location || '',
      
      // Description and notes
      Description: data.description || data.content || '',
      Notes: data.projectName ? `Project: ${data.projectName}` : '',
      
      // Source information
      source: sourceLabel,
      sourceUrl: source.url || '',
      sourceProvider: source.provider,
      sourceConfidence: source.confidence || 0.7,
      
      // GovSpending specific fields
      ...(data.contractValue && { contractValue: data.contractValue }),
      ...(data.contractDate && { contractDate: data.contractDate }),
      ...(data.projectName && { projectName: data.projectName }),
      
      // Metadata
      metadata: {
        ...data.metadata,
        providerId: result.id,
        retrievedAt: new Date().toISOString()
      },
      
      // Lead scoring (can be enhanced)
      Score: Math.round((source.confidence || 0.7) * 100),
      
      // Status
      Status: 'New',
      Stage: 'Lead'
    };
  });
};

/**
 * Helper: Group results by provider
 * @param {Array} results - Search results
 * @returns {object} Results grouped by provider
 */
export const groupResultsByProvider = (results) => {
  const grouped = {};
  
  for (const result of results) {
    const provider = result.source?.provider || 'unknown';
    if (!grouped[provider]) {
      grouped[provider] = [];
    }
    grouped[provider].push(result);
  }
  
  return grouped;
};

/**
 * Helper: Calculate statistics from metadata
 * @param {object} metadata - Search/enrich/scout metadata
 * @returns {object} Formatted statistics
 */
export const calculateStats = (metadata) => {
  const stats = {
    totalResults: metadata.totalResults || 0,
    successfulProviders: 0,
    failedProviders: 0,
    providers: []
  };

  const providers = metadata.providers || {};
  
  for (const [providerId, providerData] of Object.entries(providers)) {
    if (providerData.status === 'success') {
      stats.successfulProviders++;
    } else {
      stats.failedProviders++;
    }
    
    stats.providers.push({
      id: providerId,
      status: providerData.status,
      count: providerData.count || 0,
      duration: providerData.duration || 0,
      error: providerData.error
    });
  }
  
  return stats;
};

export default {
  listProviders,
  search,
  enrich,
  scout,
  testProvider,
  getConfig,
  updateConfig,
  convertResultsToLeads,
  groupResultsByProvider,
  calculateStats
};

